<script lang="ts">
	import { replaceState } from '$app/navigation';
	import type { Component } from 'svelte';

	interface Section {
		id: string;
		label: string;
		Icon: Component;
	}

	const {
		sections,
		activeSectionId,
		onSectionClick
	}: {
		sections: Section[];
		activeSectionId: string;
		onSectionClick: (id: string) => void;
	} = $props();

	let mobileContainer = $state<HTMLElement>();
	let desktopContainer = $state<HTMLElement>();

	function doOnSectionClick(id: string) {
		replaceState(`#${id}`, {});
		onSectionClick(id);
	}

	// Effect: Auto-scroll mobile nav to active item
	$effect(() => {
		if (!activeSectionId) return;
		const scrollActiveIntoView = (container: HTMLElement) => {
			const btn = container.querySelector(`[data-section="${activeSectionId}"]`) as HTMLElement;
			if (btn) {
				if (container === mobileContainer) {
					const scrollLeft = btn.offsetLeft - container.offsetWidth / 2 + btn.offsetWidth / 2;
					container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
				} else {
					const scrollTop = btn.offsetTop - container.offsetHeight / 2 + btn.offsetHeight / 2;
					container.scrollTo({ top: scrollTop, behavior: 'smooth' });
				}
			}
		};
		if (mobileContainer) scrollActiveIntoView(mobileContainer);
		if (desktopContainer) scrollActiveIntoView(desktopContainer);
	});
</script>

<aside class="relative hidden w-fit shrink-0 md:block">
	<nav
		bind:this={desktopContainer}
		aria-label="Table of Contents"
		class="sticky top-24 max-h-[calc(80vh)] scrollbar-thin scrollbar-thumb-base-300 scrollbar-track-transparent space-y-1 overflow-y-auto overscroll-contain pr-2"
	>
		<h4 class="mb-4 px-3 text-xs opacity-70">QUICK NAVIGATION</h4>
		<ul class="space-y-1">
			{#each sections as section (section.id)}
				<li>{@render DesktopSection(section)}</li>
			{/each}
		</ul>
	</nav>
</aside>

<nav
	bind:this={mobileContainer}
	aria-label="Table of Contents"
	class="scrollbar-hide sticky top-16 z-30 -mx-4 flex gap-2 overflow-x-auto border-b border-base-content/5 bg-base-100/90 px-4 py-3 backdrop-blur-md md:hidden"
>
	{#each sections as section (section.id)}
		{@render MobileSection(section)}
	{/each}
</nav>

{#snippet DesktopSection(section: Section)}
	{const isActive = $derived<boolean>(activeSectionId === section.id)}
	<a
		class={{
			'flex w-full flex-row items-center gap-2 rounded-lg px-3 py-2 text-left font-mono text-sm font-medium transition-all duration-200': true,
			'bg-primary text-primary-content shadow-sm': isActive,
			'text-base-content/70 hover:bg-base-200': !isActive
		}}
		href="#{section.id}"
		data-section={section.id}
		aria-current={isActive ? 'true' : undefined}
		onclick={(e) => {
			e.preventDefault();
			doOnSectionClick(section.id);
		}}
	>
		<section.Icon />
		{section.label.toLowerCase()}
	</a>
{/snippet}

{#snippet MobileSection(section: Section)}
	{const isActive = $derived<boolean>(activeSectionId === section.id)}
	<a
		class={{
			'btn flex flex-row items-center gap-2 rounded-full font-mono whitespace-nowrap transition-all duration-300 btn-sm': true,
			'btn-primary': isActive,
			'btn-ghost opacity-60': !isActive
		}}
		href="#{section.id}"
		data-section={section.id}
		aria-current={isActive ? 'true' : undefined}
		onclick={(e) => {
			e.preventDefault();
			doOnSectionClick(section.id);
		}}
	>
		<section.Icon />
		{section.label.toLowerCase()}
	</a>
{/snippet}

<style>
	.scrollbar-hide::-webkit-scrollbar {
		display: none;
	}
	.scrollbar-hide {
		-ms-overflow-style: none;
		scrollbar-width: none;
	}
</style>
